import { BASE_URL } from 'config';
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Input,
  Avatar,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Autocomplete,
  Link,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from 'context';
import { useTheme } from "@mui/material/styles";

const GOOGLE_MAPS_EMBED_HINT = (
  <>
    الحصول على رابط التضمين: افتح خرائط جوجل، ابحث عن الموقع، ثم من زر المشاركة اختر 'تضمين خريطة'
    <br />
    وانسخ رابط <b>iframe</b> فقط.
    <Link
      href="https://www.google.com/maps"
      target="_blank"
      rel="noopener"
      underline="hover"
      sx={{ fontWeight: "bold", ml: 1 }}
    >
      الذهاب إلى خرائط جوجل
    </Link>
  </>
);
const GOOGLE_MAPS_URL = "https://www.google.com/maps";

const NewHestoricPlace = ({ onClose, onSuccess, initialData }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    place_category_id: initialData?.place_category_id || null,
    name: initialData?.name || "",
    description: initialData?.description || "",
    map_location: initialData?.map_location || "",
    main_image: null,
    id: initialData?.id || null,
    main_imagePreview: initialData?.main_image || null,
    is_featured: initialData?.is_featured || false,
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const imageInputRef = useRef();

  useEffect(() => {
    setLoadingCategories(true);
    const token = localStorage.getItem("user-token");
    axios
      .get(BASE_URL + "/historical-places/create", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const fetchedCategories = res.data?.data?.place_categories || [];
        setCategories(fetchedCategories);
        // إذا كان هناك بيانات أولية وقيمة الفئة رقمية، نبحث عن الكائن المناسب
        if (
          initialData &&
          initialData.place_category_id &&
          typeof initialData.place_category_id === "number"
        ) {
          const matchedCategory = fetchedCategories.find(
            (cat) => cat.id === initialData.place_category_id
          );
          if (matchedCategory) {
            setFormData((prev) => ({
              ...prev,
              place_category_id: matchedCategory,
            }));
          }
        }
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCategories(false));
  }, [initialData]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        place_category_id: initialData.place_category_id || null,
        name: initialData.name || "",
        description: initialData.description || "",
        map_location: initialData.map_location || "",
        main_image: null,
        id: initialData.id || null,
        main_imagePreview: initialData.main_image || null,
        is_featured: initialData.is_featured || false,
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.place_category_id) newErrors.place_category_id = "فئة المكان مطلوبة";
    if (!formData.name.trim()) newErrors.name = "اسم الموقع مطلوب";
    if (!formData.description.trim()) newErrors.description = "الوصف مطلوب";
    // if (!formData.map_location.trim()) newErrors.map_location = "رابط الموقع على الخريطة مطلوب";
    // else if (!formData.map_location.includes("<iframe"))
    //   newErrors.map_location = "يجب لصق كود التضمين (iframe) فقط";
    if (!formData.main_image && !formData.main_imagePreview)
      newErrors.main_image = "الصورة الرئيسية مطلوبة";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCategoryChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, place_category_id: newValue }));
    setErrors((prev) => ({ ...prev, place_category_id: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && ["image/png", "image/jpeg", "image/gif", "image/jpg"].includes(file.type)) {
      setFormData((prev) => ({
        ...prev,
        main_image: file,
        main_imagePreview: URL.createObjectURL(file),
      }));
      setErrors((prev) => ({ ...prev, main_image: "" }));
    } else {
      setErrors((prev) => ({ ...prev, main_image: "يجب أن تكون الصورة بصيغة PNG أو JPG أو GIF" }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, main_image: null, main_imagePreview: null }));
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return;
    try {
      const formPayload = new FormData();
      formPayload.append("place_category_id", formData.place_category_id.id);
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      formPayload.append("map_location", formData.map_location);
      formPayload.append("is_featured", formData.is_featured ? 1 : 0);
      if (formData.main_image) formPayload.append("main_image", formData.main_image);
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      let response;
      if (formData.id) {
        response = await axios.post(
          `${BASE_URL}/historical-places/update/${formData.id}`,
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          BASE_URL + "/historical-places/store",
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        willOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      place_category_id: null,
      name: "",
      description: "",
      map_location: "",
      main_image: null,
      main_imagePreview: null,
      is_featured: false,
    });
    setErrors({});
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        mx: "auto",
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 3,
        boxShadow: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: { xs: 2, sm: 4 },
        minHeight: 420,
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      <Typography variant="h5" fontWeight="bold" mb={2} >
        {formData.id ? "تعديل موقع تاريخي" : "إضافة موقع تاريخي جديد"}
      </Typography>
      <Grid container spacing={2}>
        {/* صف الفئة واسم الموقع */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.place_category_id}>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name || ""}
              value={formData.place_category_id}
              onChange={handleCategoryChange}
              loading={loadingCategories}
              sx={{
                py: 0,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <>
                      فئة المكان <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  error={!!errors.place_category_id}
                  helperText={errors.place_category_id}
                  InputLabelProps={{
                    sx: {
                      py: 0,
                    },
                  }}
                  InputProps={{
                    sx: {
                      py: 0,
                    },
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCategories ? <CircularProgress color="inherit" size={5} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </FormControl>
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.name}>
            <TextField
              label={
                <>
                  اسم الموقع <span style={{ color: "red" }}>*</span>
                </>
              }
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />
          </FormControl>
        </Grid>
        {/* صف الوصف */}
        <Grid item size={{ xs: 12 }}>
          <FormControl fullWidth error={!!errors.description}>
            <TextField
              label="الوصف"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              fullWidth
              multiline
              minRows={4}
            />
          </FormControl>
        </Grid>
        {/* صف رابط الموقع على الخريطة */}
        <Grid item size={{ xs: 12 }}>
          <FormControl fullWidth error={!!errors.map_location}>
            <TextField
              label="رابط الموقع على الخريطة"
              name="map_location"
              value={formData.map_location}
              onChange={handleChange}
              error={!!errors.map_location}
              helperText={errors.map_location || GOOGLE_MAPS_EMBED_HINT}
              FormHelperTextProps={{ sx: { color: "text.main" } }}
              sx={{ color: "text.main" }}
              fullWidth
              placeholder="الصق كود التضمين (iframe) هنا"
            />
          </FormControl>
        </Grid>
        {/* عرض الخريطة */}
        {formData.map_location.includes("<iframe") && (
          <Grid item size={{ xs: 12 }}>
            <Box mt={1} display="flex" color="text.main" justifyContent="center">
              <div
                dangerouslySetInnerHTML={{
                  __html: formData.map_location,
                }}
                style={{ width: "100%", color: "text.main", minHeight: 300, border: "1px solid #eee", borderRadius: 8 }}
              />
            </Box>
          </Grid>
        )}
        {/* صف الصورة الرئيسية وcheckbox */}
        <Grid item size={{ xs: 12, }}>
          <FormControl fullWidth error={!!errors.main_image}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} width="100%">
              <label htmlFor="main-image-upload" style={{ width: "100%", py: 1 }}>
                <Input
                  type="file"
                  inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/gif" }}
                  onChange={handleImageChange}
                  sx={{ display: "none" }}
                  id="main-image-upload"
                  inputRef={imageInputRef}
                />
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  size="small"
                  sx={{ width: "100%", color: "text.main", borderColor: "text.border", fontSize: 13, py: 2 }}
                >
                  الصورة الرئيسية
                </Button>
              </label>
              {formData.main_imagePreview && (
                <Box position="relative" mt={1}>
                  <Avatar
                    src={
                      formData.main_imagePreview instanceof File
                        ? URL.createObjectURL(formData.main_imagePreview)
                        : formData.main_imagePreview
                    }
                    sx={{ width: 100, height: 100, border: "2px solid #ddd" }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{ position: "absolute", top: 0, right: 0, bgcolor: "white" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {errors.main_image && <FormHelperText error>{errors.main_image}</FormHelperText>}
            </Box>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} display="flex" alignItems="center" justifyContent="center">
          <Box display="flex" flexDirection="row" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_featured}
                  onChange={handleChange}
                  name="is_featured"
                  color="primary"
                />
              }
              label={
                <>
                  <span style={{ whiteSpace: "nowrap", fontSize: 15 }}>
                    مميز في الصفحة الرئيسية
                  </span>
                </>
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
          </Box>
        </Grid>
      </Grid>
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          * الحقول المطلوبة
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" color="primary" onClick={handleCancel} sx={{ px: 4, color: "primary.main", borderColor: "primary.dark" }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ px: 4, color: "white ", bgcolor: "primary.dark", "&:hover": { bgcolor: "primary.main" } }}

          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NewHestoricPlace;
